import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Text,
} from '@uncinc/uncinc-react-kitchen-sink';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { ButtonWrapper } from '../../../components';
import type { ParagraphImageTicker as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface PlayButtonProps {
  buttonClass: string;
  isTickerPaused: boolean;
  setTickerPaused: (arg0: boolean) => void;
}

const PlayButton: React.FC<PlayButtonProps> = ({
  buttonClass,
  isTickerPaused,
  setTickerPaused,
}) => {
  const { t } = useTranslation();

  return (
    <ButtonWrapper
      className={`ParagraphImageTicker__play-button ${buttonClass}`}
      inverted
      type="secondary"
      variant="icon-only"
      iconName={isTickerPaused ? 'play_arrow' : 'pause'}
      aria-label={t(`paragraphs.image_ticker.${isTickerPaused ? 'play_video' : 'pause_video'}`)}
      aria-pressed={!isTickerPaused}
      onClick={() => {
        if (isTickerPaused) {
          setTickerPaused(false);
        } else {
          setTickerPaused(true);
        }
      }}
    />
  );
};

interface ParagraphImageTickerProps {
  id?: string;
  entity: ImmutableEntity<Type>;
}

const ParagraphImageTicker: React.FC<ParagraphImageTickerProps> = ({
  id,
  entity,
}) => {
  const paragraphImageTicker = entity.toJS();

  const title = paragraphImageTicker.field_title[0]?.value;
  const body = paragraphImageTicker.field_body[0]?.processed || '';
  const images = paragraphImageTicker.field_images;
  const half = Math.ceil(images.length / 2);

  const tickerTapeOne = images.slice(0, half);
  const tickerTapeTwo = images.slice(half);

  const link = paragraphImageTicker.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const secondaryLink = paragraphImageTicker.field_link_secondary[0];
  const secondaryLinkTitle = secondaryLink?.title;
  const secondaryLinkUrl = secondaryLink?.alias || secondaryLink?.uri;

  const [isTickerPaused, setTickerPaused] = useState(false);
  const animationPlayState = isTickerPaused ? 'paused' : 'running';
  const animationDuration = images.length < 16 ? '45s' : '90s';
  const tapeRepeatCount = images.length < 12 ? 3 : 2;

  return (
    <div id={id} className="Paragraph ParagraphImageTicker">
      <Container>
        <div className="row">
          <div className="col-12 col-lg-6 col-xl-5">
            <div className="ParagraphImageTicker__body">
              <Text
                title={title}
                text={body}
              />

              <div className="ParagraphImageTicker__buttons">
                {(linkUrl || secondaryLinkUrl) && (
                  <div className="ParagraphImageTicker__links">
                    {linkUrl && (
                      <ButtonWrapper
                        type="secondary"
                        href={linkUrl}
                      >
                        {linkTitle}
                      </ButtonWrapper>
                    )}

                    {secondaryLinkUrl && (
                      <ButtonWrapper
                        type="textual"
                        inverted
                        href={secondaryLinkUrl}
                      >
                        {secondaryLinkTitle}
                      </ButtonWrapper>
                    )}
                  </div>
                )}
                <PlayButton
                  buttonClass="side-button"
                  isTickerPaused={isTickerPaused}
                  setTickerPaused={setTickerPaused}
                />
              </div>
            </div>
          </div>
          <div className="ParagraphImageTicker__center-column col-lg-1">
            <PlayButton
              buttonClass="center-button"
              isTickerPaused={isTickerPaused}
              setTickerPaused={setTickerPaused}
            />
          </div>
          <div className="col-full-lg col-lg-5 col-xl-6">
            <div className="ParagraphImageTicker__outer-wrapper">
              <div className="ParagraphImageTicker__ticker-wrapper ParagraphImageTicker__ticker-wrapper__one">
                {[...Array(tapeRepeatCount)].map(() => (
                  <div
                    className="ParagraphImageTicker__ticker-tape ParagraphImageTicker__ticker-tape__one"
                    style={{
                      animationDuration,
                      animationPlayState,
                    }}
                  >
                    {tickerTapeOne.map((image) => (
                      <div className="ParagraphImageTicker__image-wrapper">
                        <DynamicMediaItem
                          media={image}
                          imageAspectRatio="original"
                          alt=""
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="ParagraphImageTicker__ticker-wrapper ParagraphImageTicker__ticker-wrapper__two">
                {[...Array(tapeRepeatCount)].map(() => (
                  <div
                    className="ParagraphImageTicker__ticker-tape ParagraphImageTicker__ticker-tape__two"
                    style={{
                      animationDuration,
                      animationPlayState,
                    }}
                  >
                    {tickerTapeTwo.map((image) => (
                      <div className="ParagraphImageTicker__image-wrapper">
                        <DynamicMediaItem
                          media={image}
                          imageAspectRatio="original"
                          alt=""
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="ParagraphImageTicker__overlay" />
            </div>
          </div>

        </div>

      </Container>
    </div>
  );
};

export default ParagraphImageTicker;
